import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Image as ImageIcon } from 'lucide-react';
import type { PhotoRecordDto } from '@forest/contracts';
import { useParcelPhotos } from './usePhotos';
import { useParcelDetail } from '@/features/parcels/useParcels';
import { driveThumb } from '@/lib/driveUrl';

type ParcelSummary = {
  id: string;
  ucf?: string | null;
  codProiect?: string | null;
  raion?: string | null;
  primarie?: string | null;
  anul?: number | null;
};

type Props = {
  parcel: ParcelSummary | null;
  onClose: () => void;
  onSelectPhoto: (photo: PhotoRecordDto) => void;
};

/**
 * One labeled row inside the polygon-details grid. Surfaces `—` when
 * the value is missing so an empty cell never collapses the
 * adjacent rows visually.
 */
function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 last:border-b-0">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span
        className={`min-w-0 truncate text-right text-xs ${
          mono ? 'font-mono' : ''
        } ${value === '—' ? 'text-slate-400' : 'text-slate-800'}`}
        title={typeof value === 'string' ? value : undefined}
      >
        {value || '—'}
      </span>
    </div>
  );
}

/**
 * Polygon-photos side panel. Opens when a parcel polygon (or its UCF label)
 * is clicked on the map. Lists photo thumbnails associated with the parcel,
 * ordered by creation date descending.
 */
export function ParcelPhotosPanel({ parcel, onClose, onSelectPhoto }: Props) {
  const { t } = useTranslation(['photos', 'filter', 'common', 'database']);

  useEffect(() => {
    if (!parcel) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Claim the key so an enclosing Escape handler (the map's
      // fullscreen shell) doesn't also act on it.
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [parcel, onClose]);

  // useParcelPhotos hits `GET /api/parcels/:id/photos` which returns
  // BOTH photos linked by parcelId AND photos whose GPS falls inside
  // the polygon — without that bbox+PiP fallback, photos uploaded
  // before the polygon existed (xlsx import predates a later shapefile
  // import for that area) stayed hidden because their parcelId is
  // still null. The endpoint already handles auth scope.
  const query = useParcelPhotos(parcel?.id ?? null);
  // Full parcel record — pulls fields the click-derived summary
  // doesn't carry (entitateSilvica, ocolSilvic, suprafata, etc.) so
  // the details section can render everything available. Cached for
  // 60s by useParcelDetail; multiple opens of the same parcel are
  // free.
  const detailQuery = useParcelDetail(parcel?.id ?? null);

  if (!parcel) return null;

  const photos = query.data ?? [];
  const detail = detailQuery.data;

  return (
    <>
      <div className="fixed inset-0 z-[1100] bg-black/30 lg:hidden" onClick={onClose} />
      <aside
        className={[
          'fixed z-[1150] flex flex-col bg-white shadow-2xl',
          'inset-x-0 bottom-0 max-h-[80vh] rounded-t-xl',
          'lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-0 lg:h-screen lg:max-h-none lg:w-96 lg:max-w-[420px] lg:rounded-none',
        ].join(' ')}
        role="dialog"
        aria-label={t('filter:parcelPanel.title', { defaultValue: 'Fotografii în poligon' })}
      >
        <header className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {t('filter:parcelPanel.title', { defaultValue: 'Fotografii în poligon' })}
            </p>
            <h2 className="truncate text-sm font-semibold text-slate-800">
              {parcel.codProiect ?? '—'} · UCF {parcel.ucf ?? '—'}
            </h2>
            <p className="truncate text-xs text-slate-500">
              {[parcel.raion, parcel.primarie].filter(Boolean).join(' · ') || '—'}
              {parcel.anul ? ` · ${parcel.anul}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {/* === Polygon details (always visible) =================== */}
          <section className="border-b border-slate-200 px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
              {t('filter:parcelPanel.details', {
                defaultValue: 'Date poligon',
              })}
            </p>
            <DetailRow
              label={t('database:table.header.raion', { defaultValue: 'Raion' })}
              value={detail?.raion ?? parcel.raion ?? '—'}
            />
            <DetailRow
              label={t('database:table.header.primarie', { defaultValue: 'Primărie' })}
              value={detail?.primarie ?? parcel.primarie ?? '—'}
            />
            <DetailRow
              label={t('database:table.header.entitateSilvica', {
                defaultValue: 'Entitate silvică',
              })}
              value={detail?.entitateSilvica ?? '—'}
            />
            <DetailRow
              label={t('database:table.header.ocolSilvic', {
                defaultValue: 'Ocol silvic',
              })}
              value={detail?.ocolSilvic ?? '—'}
            />
            <DetailRow
              label={t('database:table.header.ucf', { defaultValue: 'UCF' })}
              value={detail?.ucf ?? parcel.ucf ?? '—'}
              mono
            />
            <DetailRow
              label={t('database:table.header.codProiect', {
                defaultValue: 'Cod proiect',
              })}
              value={detail?.codProiect ?? parcel.codProiect ?? '—'}
              mono
            />
            <DetailRow
              label={t('database:table.header.suprafata', {
                defaultValue: 'Suprafață (ha)',
              })}
              value={
                detail?.suprafata != null
                  ? detail.suprafata.toFixed(2)
                  : '—'
              }
              mono
            />
            <DetailRow
              label={t('database:table.header.anul', { defaultValue: 'An' })}
              value={detail?.anul ?? parcel.anul ?? '—'}
              mono
            />
            {detailQuery.isLoading ? (
              <p className="mt-2 text-[10px] italic text-slate-400">
                {t('common:loading', { defaultValue: 'Se încarcă...' })}
              </p>
            ) : null}
          </section>

          {/* === Photos in polygon ================================== */}
          <section className="px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-blue-700">
              {t('filter:parcelPanel.title', {
                defaultValue: 'Fotografii în poligon',
              })}
              {query.data ? (
                <span className="ml-2 font-mono text-[10px] text-blue-500">
                  {photos.length}
                </span>
              ) : null}
            </p>
            {query.isLoading ? (
              <p className="p-2 text-center text-xs text-slate-500">
                {t('common:loading', { defaultValue: 'Se încarcă...' })}
              </p>
            ) : photos.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
                <ImageIcon className="h-6 w-6 text-slate-300" />
                {t('filter:parcelPanel.empty', {
                  defaultValue: 'Nicio fotografie în acest poligon',
                })}
              </div>
            ) : (
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {photos.map((photo) => (
                  <li key={photo.id}>
                    <button
                      type="button"
                      onClick={() => onSelectPhoto(photo)}
                      className="group block w-full overflow-hidden rounded border border-slate-200 bg-white text-left shadow-sm transition hover:border-emerald-500 hover:shadow"
                    >
                      <div className="aspect-square w-full overflow-hidden bg-slate-100">
                        {photo.downloadUrl ? (
                          <img
                            src={driveThumb(photo.downloadUrl, 300)}
                            alt={photo.fileName}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            className="h-full w-full object-cover transition group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="px-1.5 py-1">
                        <p className="truncate text-[11px] font-medium text-slate-800">
                          {photo.fileName}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {formatDate(photo.createdAt)}
                        </p>
                        {photoMeta(photo) ? (
                          <p
                            className="truncate text-[10px] text-slate-500"
                            title={photoMeta(photo) ?? undefined}
                          >
                            {photoMeta(photo)}
                          </p>
                        ) : null}
                        {photo.ultimaLucrare ? (
                          <p
                            className="truncate text-[10px] text-slate-500"
                            title={photo.ultimaLucrare}
                          >
                            {photo.ultimaLucrare}
                          </p>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

/**
 * Compact one-line summary under a photo tile: author (cod, falling
 * back to the full name), base species, photo year. Null when the
 * photo carries none of them so the tile stays short.
 */
function photoMeta(photo: PhotoRecordDto): string | null {
  const parts = [
    photo.author?.cod ?? photo.author?.fullName,
    photo.speciaDeBaza,
    photo.year,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}
